import { useEffect } from "react";
import {
  experimentClient,
  ExperimentUserProperties,
} from "core/experiment/experimentClient";

interface UseAmplitudeExperimentParams {
  userId: string | null;
  userProperties?: ExperimentUserProperties;
}

/**
 * Warm-up hook that initializes the Amplitude Experiment client once the
 * authenticated user ID is available. Call this once in App.tsx after useAuth resolves.
 *
 * Two-phase lifecycle:
 *  1. First effect run with a non-null userId → experimentClient.init(...) boots the SDK.
 *     userProperties may be partially undefined here (e.g., `application` is still loading).
 *  2. Subsequent effect runs (userProperties content changes) → experimentClient.updateUser(...)
 *     refreshes the user via setUser() + fetch() so targeting rules see the real values.
 *
 * useFeatureFlag hooks are correct even if this hook runs after them — the deferred
 * resolver pattern in experimentClient ensures variant reads await ensureStarted().
 *
 * Sync errors from init() (e.g., Experiment.initialize throwing) are non-fatal: flag
 * reads degrade to enabled=false via ensureStarted(). Async start() failures are
 * handled inside experimentClient itself.
 */
export function useAmplitudeExperiment({
  userId,
  userProperties,
}: UseAmplitudeExperimentParams): void {
  // Deep-compare userProperties by serialized content so a stable object identity
  // with the same field values does not trigger updateUser; a content change does.
  const userPropertiesKey = JSON.stringify(userProperties ?? {});

  useEffect(() => {
    if (!userId) return;

    if (experimentClient.getInstance()) {
      experimentClient.updateUser({ userId, userProperties });
    } else {
      // init() may reject with a sync error from Experiment.initialize; swallowing it
      // here matches the degrade-to-disabled contract documented on init().
      experimentClient.init({ userId, userProperties }).catch(() => {});
    }
    // userProperties intentionally tracked via JSON key, not by reference identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userPropertiesKey]);
}
